import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import ArrowDropDown from '@mui/icons-material/ArrowDropDown'
import { Box, Button, Menu, Stack, Typography } from '@mui/material'
import { ReactFlowProvider } from '@xyflow/react'
import useOverlay from '@/hooks/useOverlay'
import { useGetProjectGrids } from '@/service-library/hooks/project-grids'
import { useGetProjectLinkedModelInfoTypes } from '@/service-library/hooks/project-linked-model-info-types'
import {
  useDeleteProjectLinkedModel,
  useGetProjectLinkedModel,
  useUpdateProjectLinkedModel,
} from '@/service-library/hooks/project-linked-models'
import {
  useGetProjectModels,
  useUpdateProjectModel,
} from '@/service-library/hooks/project-models'
import { useGetPubModels } from '@/service-library/hooks/pub-models'
import queryKeys from '@/service-library/query-keys'
import useIsSuperUser from '@/services/hooks/useIsSuperUser'
import { ProjectLinkedModel } from '@/types/project-linked-models'
import { ProjectModel, ProjectModelVersion } from '@/types/project-models'
import { showSuccessSnackbar } from '@/utils/snackbars'
import CopyIDButton from '@/components/copy-id-button/CopyIDButton'
import ModelVersionSelect from '@/components/model-version-select/ModelVersionSelect'
import ChildDataMenuBody from '@/components/navigation/ChildDataMenuBody'
import ProjectBreadcrumb from '@/components/project-tables/ProjectBreadcrumb'
import SettingsPage from '@/components/shared/settings/SettingsPage'
import SettingsRow from '@/components/shared/settings/SettingsRow'
import SettingsSection from '@/components/shared/settings/SettingsSection'
import ModelVersionInputsOutputsDisplay from '../model-inputs-outputs/ModelVersionInputsOutputsDisplay'
import QueryKeyProvider from '../model-inputs-outputs/QueryKeyProvider'
import { useProjectContext } from '../project-tables/ProjectProvider'

const backToLibrary = { label: 'Back to Model Library', to: '../model-library' }

const labelMap: Record<string, string> = {
  OCR: 'OCR model',
  NER: 'Labeling model',
  Category: 'Category model',
}

// This is where users can manage a ProjectLinkedModel.
export default function ModelPage() {
  const { id } = useParams()
  const { project } = useProjectContext()
  const navigate = useNavigate()
  const modelsOverlay = useOverlay()
  const modelsAnchorRef = useRef<HTMLButtonElement>(null)

  const isSuperUser = useIsSuperUser()

  const [selectedModelId, setSelectedModelId] = useState<string | null>(null)

  const { projectLinkedModel, queryKey, isPending } = useGetProjectLinkedModel({
    id: id as string,
    filters: {
      fields__include: 'project_model',
      project_model__fields__include:
        'project_model_type,project_model_versions,has_children',
      project_model__project_model_versions__fields__include:
        'project_model_version_info_types',
      project_model__project_model_versions__project_model_version_info_types__fields__include:
        'info_type',
      project_model__project_model_versions__project_model_version_info_types__info_type__fields__include:
        'id,name,namespace',
    },
  })

  // We fetch the project grids here instead of using them off project context project.project_grids
  // so we can get the info_type with the fields.
  const { projectGrids, isPending: isLoadingGrids } = useGetProjectGrids({
    filters: {
      limit: '1000',
      project_id: project.id,
      fields__include: 'project_grid_fields',
      project_grid_fields__fields__include: 'info_type',
      project_grid_fields__info_type__fields__only: 'id,name',
    },
  })

  useEffect(() => {
    if (!selectedModelId && projectLinkedModel?.project_model?.has_children) {
      setSelectedModelId(projectLinkedModel.project_model_id)
    }
  }, [projectLinkedModel, selectedModelId])

  const { projectModels: childModels, queryKey: childModelsQueryKey } =
    useGetProjectModels({
      filters: {
        parent_model_id: projectLinkedModel?.project_model_id,
        fields__include: 'project_model_type,project_model_versions',
        project_model_versions__fields__include:
          'project_model_version_info_types',
        project_model_versions__project_model_version_info_types__fields__include:
          'info_type',
      },
      enabled: !!projectLinkedModel?.project_model?.has_children,
    })

  const {
    projectLinkedModelInfoTypes,
    queryKey: pLMInfoTypesQueryKey,
    isPending: isLoadingLinkedInfoTypes,
  } = useGetProjectLinkedModelInfoTypes({
    filters: {
      project_linked_model_id: id as string,
    },
    enabled: !!projectLinkedModel,
  })

  const { updateProjectLinkedModel } = useUpdateProjectLinkedModel({
    detailQueryKey: queryKey,
    onSuccess: () => {
      showSuccessSnackbar('Model version updated')
    },
  })

  const { deleteProjectLinkedModel } = useDeleteProjectLinkedModel({
    sideEffectQueryKeys: [queryKeys.projectLinkedModels.all],
    onSuccess: () => {
      showSuccessSnackbar('Model removed from project')
    },
  })

  const { updateProjectModel } = useUpdateProjectModel({
    listQueryKey: childModels.length ? childModelsQueryKey : undefined,
    sideEffectQueryKeys: [queryKey],
    onSuccess: () => {
      showSuccessSnackbar('Model version updated')
    },
  })

  const { pubModels, isPending: isLoadingPubModels } = useGetPubModels({
    filters: {
      limit: '1',
      project_model_id: projectLinkedModel?.project_model_id,
      fields__include: 'pub_model_versions',
    },
    enabled: !!projectLinkedModel,
  })

  if (!projectLinkedModel && !isPending)
    return (
      <>
        <ProjectBreadcrumb label="Model Library" url="../model-library" />
        <SettingsPage
          back={backToLibrary}
          title="Model not found"
          description="Contact Pixydocs support if this is unexpected."
        />
      </>
    )

  if (
    !projectLinkedModel ||
    isLoadingPubModels ||
    isLoadingGrids ||
    isLoadingLinkedInfoTypes
  )
    return (
      <Stack alignItems="center" sx={{ mt: 8 }}>
        <Typography variant="h6" color="text.secondary">
          Loading...
        </Typography>
      </Stack>
    )

  const selectedModel = selectedModelId
    ? [projectLinkedModel?.project_model, ...childModels].find(
        (m) => m?.id === selectedModelId,
      )
    : projectLinkedModel?.project_model

  const pubModelVersions = pubModels[0]?.pub_model_versions || []
  const selectedProjectModelVersions = selectedModel?.project_model_versions

  const showAsPubModel =
    projectLinkedModel?.project_model?.project_id !== project.id

  const selectedVersionId = showAsPubModel
    ? projectLinkedModel.current_model_version_id || 'current'
    : selectedModel?.current_version_id

  const recommendedPubModelVersion = pubModelVersions.find(
    (v) => v.id === pubModels[0]?.recommended_version_id,
  )
  const recommendedPubModelProjectModelVersion =
    selectedProjectModelVersions?.find(
      (v) => v.id === recommendedPubModelVersion?.model_version_id,
    )

  const validVersions = showAsPubModel
    ? pubModelVersions?.map((version) => {
        const ver = selectedProjectModelVersions?.find(
          (v) => v.id === version.model_version_id,
        )
        return {
          ...ver,
          id: version.model_version_id,
          version: parseFloat(
            `${version.version[0]}.${version.version[1] || 0}`,
          ),
          training_status: 'completed',
          is_current: recommendedPubModelProjectModelVersion?.id === ver?.id,
        } as ProjectModelVersion
      })
    : selectedProjectModelVersions?.filter(
        (v) => v.training_status === 'completed',
      )

  function handleUpdateCurrentVersion(versionId: string) {
    if (showAsPubModel) {
      updateProjectLinkedModel({
        ...projectLinkedModel,
        current_model_version_id: versionId === 'current' ? null : versionId,
      } as ProjectLinkedModel)
    } else {
      updateProjectModel({
        ...selectedModel,
        current_version_id: versionId === 'current' ? null : versionId,
      } as ProjectModel)
    }
  }

  let displayedVersion = validVersions?.find((v) => v.id === selectedVersionId)

  if (!displayedVersion && showAsPubModel && selectedVersionId === 'current') {
    displayedVersion = recommendedPubModelProjectModelVersion
  }

  const modelName = showAsPubModel
    ? pubModels[0]?.name
    : projectLinkedModel.project_model?.name

  const hasChildren = !!projectLinkedModel.project_model?.has_children

  const typeLabel =
    labelMap[
      projectLinkedModel.project_model?.project_model_type?.code || 'ner'
    ]

  let ownershipNote = ''
  if (!showAsPubModel) {
    ownershipNote =
      pubModels.length > 0
        ? 'Published, but owned by this project. Changes here only affect how this project uses it, not other projects.'
        : 'Managed by this project.'
  }

  return (
    <QueryKeyProvider queryKey={pLMInfoTypesQueryKey}>
      <ProjectBreadcrumb label="Model Library" url="../model-library" />
      <ProjectBreadcrumb label={projectLinkedModel.project_model?.name || ''} />

      <SettingsPage
        back={backToLibrary}
        title={modelName}
        description={[typeLabel, ownershipNote].filter(Boolean).join('. ')}
        width="lg"
        actions={
          isSuperUser && (
            <CopyIDButton stringToCopy={projectLinkedModel.id} isSuperUser />
          )
        }
      >
        <SettingsSection title="Model version">
          {/* Not `hasChildren`, so TS narrows `project_model` below. */}
          {projectLinkedModel.project_model?.has_children && (
            <SettingsRow
              label="Project model"
              description="The model in this group to set the version for."
            >
              <Button
                ref={modelsAnchorRef}
                variant="outlined"
                color="inherit"
                endIcon={<ArrowDropDown />}
                onClick={modelsOverlay.open}
                aria-controls={modelsOverlay.isOpen ? 'models-menu' : undefined}
                aria-haspopup="true"
              >
                {selectedModel?.name}
              </Button>
              <Menu
                id="models-menu"
                anchorEl={modelsAnchorRef.current}
                open={modelsOverlay.isOpen}
                onClose={modelsOverlay.close}
              >
                <ChildDataMenuBody
                  rootObject={projectLinkedModel.project_model}
                  objects={childModels}
                  subheader="Project models"
                  noOptionsText="No models found"
                  onClick={(objectId) => {
                    modelsOverlay.close()
                    setSelectedModelId(objectId)
                  }}
                />
              </Menu>
            </SettingsRow>
          )}

          <SettingsRow
            label="Version"
            description={
              !hasChildren && showAsPubModel && selectedVersionId === 'current'
                ? 'Used during prediction. If a new recommended version is released, the project switches to it.'
                : 'Used during prediction.'
            }
          >
            {hasChildren ? (
              <ModelVersionSelect
                selectedVersionId={selectedVersionId || 'no-versions'}
                setSelectedVersionId={handleUpdateCurrentVersion}
                modelVersions={validVersions as ProjectModelVersion[]}
              />
            ) : (
              <ModelVersionSelect
                selectedVersionId={selectedVersionId || 'no-versions'}
                setSelectedVersionId={handleUpdateCurrentVersion}
                modelVersions={validVersions as ProjectModelVersion[]}
                showNullOption={showAsPubModel}
                nullOptionLabel={
                  recommendedPubModelProjectModelVersion
                    ? `Use recommended version (v${
                        validVersions?.find((v) => v.is_current)?.version ?? 1
                      }.0)`
                    : 'None'
                }
                showCurrentVersion={showAsPubModel}
                currentVersionLabel="Recommended version"
              />
            )}
          </SettingsRow>
        </SettingsSection>

        {displayedVersion && (
          <SettingsSection
            title="Inputs and outputs"
            description={`The expected inputs and outputs for version v${displayedVersion.version}.`}
            variant="plain"
          >
            <Box sx={{ height: 500 }}>
              <ReactFlowProvider>
                <ModelVersionInputsOutputsDisplay
                  key={displayedVersion.id}
                  projectLinkedModel={projectLinkedModel}
                  projectModelVersionInfoTypes={
                    displayedVersion?.project_model_version_info_types
                  }
                  projectGrids={projectGrids}
                  projectLinkedModelInfoTypes={projectLinkedModelInfoTypes}
                />
              </ReactFlowProvider>
            </Box>
          </SettingsSection>
        )}

        <SettingsSection title="Danger zone" tone="danger">
          <SettingsRow
            label="Remove from library"
            description="This project can no longer use the model. The model itself isn't deleted."
          >
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                deleteProjectLinkedModel(projectLinkedModel.id)
                navigate('../model-library')
              }}
            >
              Remove from library
            </Button>
          </SettingsRow>
        </SettingsSection>
      </SettingsPage>
    </QueryKeyProvider>
  )
}
